import { useMemo, useState } from 'react'
import { InputText } from '@/components/commons/input-text'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { useQuickAssignNeighborhood } from '@/features/subgeo/queries/use-quick-assign-neighborhood'
import { useRemoveNeighborhood } from '@/features/subgeo/queries/use-remove-neighborhood'
import { useSubgeoStore } from '@/features/subgeo/store/subgeo-store'
import type { Subgeo } from '@/features/subgeo/types'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

interface NeighborhoodActionsProps {
	neighborhoodId: string
	currentSubgeo?: Subgeo | null
	subgeos: Subgeo[]
	showActionButtons?: boolean
	onClose?: () => void
}

export const NeighborhoodActions = ({
	neighborhoodId,
	currentSubgeo,
	subgeos,
	showActionButtons = true,
	onClose
}: NeighborhoodActionsProps) => {
	const { t } = useTranslation()
	const { subgeoColorMap, startEditingSubgeo } = useSubgeoStore()
	const [search, setSearch] = useState('')

	const { mutate: quickAssign } = useQuickAssignNeighborhood()
	const { mutate: removeNeighborhood } = useRemoveNeighborhood()

	const onSearch = debounce((value: string) => {
		setSearch(value)
	}, 350)

	const filteredSubgeos = useMemo(() => {
		const availableSubgeos = subgeos.filter(
			(s) => s.id !== currentSubgeo?.id
		)
		if (!search) {
			return availableSubgeos
		}
		const searchLower = search.toLowerCase()
		return availableSubgeos.filter((s) =>
			s.name.toLowerCase().includes(searchLower)
		)
	}, [subgeos, search, currentSubgeo])

	const handleAssignToSubgeo = (subgeoId: string) => {
		quickAssign({
			neighborhoodId,
			targetSubgeoId: subgeoId
		})
		onClose?.()
	}

	const handleRemoveFromSubgeo = () => {
		if (!currentSubgeo) {
			return
		}
		removeNeighborhood({
			neighborhoodId,
			currentSubgeoId: currentSubgeo.id
		})
		onClose?.()
	}

	const handleEditCorrespondingSubgeo = () => {
		if (!currentSubgeo) {
			return
		}
		startEditingSubgeo(currentSubgeo.id)
		onClose?.()
	}

	return (
		<>
			{showActionButtons && currentSubgeo && (
				<div className='flex w-full gap-2'>
					<ButtonLegacy
						className='w-full'
						onClick={handleEditCorrespondingSubgeo}
						size='sm'
						type='button'
						variant='secondary'
					>
						{t('subgeo:edit_corresponding_subgeo')}
					</ButtonLegacy>
					<ButtonLegacy
						className='w-full'
						onClick={handleRemoveFromSubgeo}
						size='sm'
						type='button'
						variant='secondaryDanger'
					>
						{t('common:remove')}
					</ButtonLegacy>
				</div>
			)}

			<div className='w-full py-2'>
				<InputText
					autoFocus
					inputSize='sm'
					label={t('subgeo:add_ngbhd_to_subgeo')}
					onChange={(e) => onSearch(e.target.value)}
					placeholder={t('subgeo:search_subgeos')}
					type='text'
				/>
			</div>

			{filteredSubgeos.length > 0 ? (
				<div className='flex max-h-[400px] w-full flex-col gap-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar-thumb]:hover:bg-neutral-400 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'>
					{filteredSubgeos.map((subgeo) => {
						const color = subgeoColorMap.get(subgeo.id)
						return (
							<button
								className='w-full cursor-pointer rounded-lg border-none bg-transparent p-1.5 text-left transition-colors hover:bg-neutral-50'
								key={subgeo.id}
								onClick={() => handleAssignToSubgeo(subgeo.id)}
								type='button'
							>
								<div className='flex items-center gap-3'>
									<div
										className='h-3 w-3 shrink-0 rounded-full'
										style={{
											backgroundColor:
												color?.fill || '#3B82F6'
										}}
									/>
									<span className='font-medium text-neutral-900 text-sm'>
										{subgeo.name}
									</span>
								</div>
							</button>
						)
					})}
				</div>
			) : (
				<div className='p-3 text-center text-neutral-500 text-sm'>
					{t('subgeo:no_subgeos')}
				</div>
			)}
		</>
	)
}
